import { Component, TemplateFunction } from '@ribajs/core'
import { hasChildNodesTrim, sleep } from '@ribajs/utils'
import { isInMarketAllowlist, getTextColorForBackground } from '../utils'
import { ShopifyProductService, ShopifyCartService } from '../services'
import { SbCartPresentsSlideshowComponent, SbPopupComponent } from '.'
import { POPUP_PRESENTS_ID } from '../constants'

import type { ShopifyCartLineItem, SbCartPresent } from '../types'

interface Scope {
  // OPTIONS
  cartItems: ShopifyCartLineItem[]
  cartTotalPrice: number
  cartOriginalTotalPrice: number
  /**
   * `true` if the popup toggle button should be visible.
   */
  visible: boolean
  presents: SbCartPresent[]
  /**
   * If `true`, the popup will be shown automatically as soon as the customer has reached the free presents total price.
   */
  autoShowPopup: boolean
  popupAlreadyShown: boolean
  /** Show "Choose gift" button */
  showButton: boolean
  buttonClass: string

  // METHODS
  togglePopup: SbCartPresentsNotificationComponent['togglePopup']
  closePopup: SbCartPresentsNotificationComponent['closePopup']
  openPopup: SbCartPresentsNotificationComponent['openPopup']

  // TEMPLATES
}

/**
 * Notify the customer about `presents` by opening a popup and toast.
 * This component holds the most logic for the `presents` and interacts with the the `sb-cart-presents-slideshow` component to define the `presents` for the popup.
 */
export class SbCartPresentsNotificationComponent extends Component {
  public static tagName = 'sb-cart-presents-notification'

  public slideshow: SbCartPresentsSlideshowComponent | null = null

  public popup: SbPopupComponent | null = null

  /** Listen for cart events */
  protected cartEvents = ShopifyCartService.events

  fullReady: boolean = false

  public scope: Scope = {
    // OPTIONS
    cartItems: [],
    cartTotalPrice: 0,
    cartOriginalTotalPrice: 0,
    visible: false,
    presents: [],
    autoShowPopup: true,
    popupAlreadyShown: false,
    showButton: false,
    buttonClass: '',

    // METHODS
    togglePopup: this.togglePopup.bind(this),
    closePopup: this.closePopup.bind(this),
    openPopup: this.openPopup.bind(this),

    // TEMPLATES
  }

  static get observedAttributes(): string[] {
    return [
      'presents',
      'cart-total-price',
      'cart-original-total-price',
      'auto-show-popup',
    ]
  }

  protected requiredAttributes(): string[] {
    return ['cart-items']
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCartPresentsNotificationComponent.observedAttributes)
    this.addEventListeners()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected parsedAttributeChangedCallback() {
    this.update()
  }

  protected update() {
    if (this.scope.presents.length === 0) return
    const visible = this.shouldBeVisible()

    const notify = this.scope.visible === false && visible === true

    if (notify) {
      // Show the popup if the popup should automatically be shown and the cart total price has reached the free presents total price
      if (this.scope.autoShowPopup && this.scope.popupAlreadyShown === false) {
        this.openPopup()
        this.scope.popupAlreadyShown = true
      }
    }

    this.scope.visible = visible
    this.slideshow?.setPresents(this.scope.presents)

    this.scope.showButton = this.scope.presents.some(present => present.visible)

    // Get popup variant and set button class based on it
    if (this.popup?.scope.variant) {
      const variant = this.popup?.scope.variant
      const textColor = getTextColorForBackground(variant)
      this.scope.buttonClass = `bg-${variant} hover:bg-${variant} ${textColor.join(' ')}`
    }
  }

  async beforeBind() {
    await super.beforeBind()
    await this.preparePresents()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()

    this.slideshow = document.querySelector(
      SbCartPresentsSlideshowComponent.tagName,
    )
    if (!this.slideshow) {
      throw new Error(
        `Could not find ${SbCartPresentsSlideshowComponent.tagName} component`,
      )
    }

    this.popup = document.getElementById(
      POPUP_PRESENTS_ID,
    ) as SbPopupComponent | null

    // WORKAROUND: Wait for the popup to be ready
    if (!this.popup) {
      await sleep(1000)
      this.popup = document.getElementById(
        POPUP_PRESENTS_ID,
      ) as SbPopupComponent | null
    }

    if (!this.popup) {
      throw new Error('Could not find popup component')
    }

    this.update()

    this.fullReady = true
    this.classList.add(`${SbCartPresentsNotificationComponent.tagName}-ready`)
  }

  /** Called from `SbCartProgressBarsComponent` */
  public getPresents() {
    return this.scope.presents
  }

  /**
   * Check if the cart has a product from the present, if yes, this means that the gift is already included.
   * @param present
   * @returns
   */
  public cartHasProductFromPresent(present: SbCartPresent) {
    return !!this.scope.cartItems.find(item => {
      return present.products?.find(product => {
        if (product.variants) {
          let foundItem: ShopifyCartLineItem | undefined
          // Only allowed variants from `present.productVariantIds` in this array, see `preparePresents`
          const foundProduct = product.variants.find(variant => {
            if (variant.id === item.variant_id) {
              foundItem = item
              return true
            }
            return false
          })

          // Check if line item is really a present
          if (foundProduct && foundItem?.final_line_price === 0) {
            return true
          }
          return false
        }
        return false
      })
    })
  }

  /**
   * Returns `true` if any presents group should be visible.
   * Used to show the popup, presents slideshow and the popup toggle button.
   */
  public shouldBeVisible() {
    let visible = false
    for (const present of this.scope.presents) {
      visible = this.shouldBeVisibleForPresent(present) || visible
    }
    return visible
  }

  /**
   * Returns true if the present should be visible.
   * This is the case if:
   * * The present has products
   * * The cart total price is greater or equal than the `present.fromTotal`
   * * And the cart has no product from the present.
   * @param present
   * @returns
   */
  public shouldBeVisibleForPresent(present: SbCartPresent) {
    const total = this.scope.cartTotalPrice // this.scope.cartOriginalTotalPrice
    present.visible =
      present.products &&
      present.products.length > 0 &&
      present.fromTotal > 0 &&
      total > 0 &&
      total >= present.fromTotal &&
      !this.cartHasProductFromPresent(present)

    return present.visible
  }

  /**
   * Set the products for each present.
   * On init, the `presents` have only product handles.
   * See `shopify/snippets/utils-json-cart-presents.liquid` to see how the presents array is defined
   */
  protected async preparePresents() {
    this.scope.presents = this.scope.presents.filter(present => {
      return present.fromTotal > 0 && isInMarketAllowlist(present.markets)
    })

    for (const present of this.scope.presents) {
      // Trim product variant ids and convert to number
      present.productVariantIds = (present.productVariantIds || []).map(id =>
        Number((id as unknown as string).trim()),
      )

      present.products = await ShopifyProductService.get(present.productHandles)
    }

    return this.scope.presents
  }

  protected addEventListeners() {
    this.cartEvents.on('ShopifyCart:request:changed', this.onCartChanged, this)
  }

  protected removeEventListeners() {
    this.cartEvents.off('ShopifyCart:request:changed', this.onCartChanged, this)
  }

  public togglePopup() {
    this.popup?.toggle()
  }

  public closePopup() {
    this.popup?.hide()
  }

  public openPopup() {
    this.popup?.show()
  }

  /** Called if the cart has changed */
  protected onCartChanged(/*cart: ShopifyCartObject*/) {
    this.closePopup()
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    // Use this template if this custom element has no child html
    if (!hasChildNodesTrim(this)) {
      return (
        <p class="error">
          This component has no it's own template, you need to add your own
          child HTML
        </p>
      )
    }
    return null
  }
}
